import { useState, useRef, useEffect } from 'react';
import { Plus, FileText, Plane, DollarSign, Sparkles, X, Settings, Briefcase } from 'lucide-react';
import { getQuickActionById } from '../../config/quickActions';
import { areNavigationPathsAllowed } from '../../utils/navigationSearch';

interface QuickAction {
  id: string;
  label: string;
  icon: React.ElementType;
  action: () => void;
  category: 'booking' | 'planning' | 'ai' | 'finance' | 'other';
  permissionPaths: readonly string[];
}

interface QuickActionsMenuProps {
  onNavigate: (path: string) => void;
  allowedPaths: ReadonlySet<string>;
  onCreatePNR?: () => void;
  onCreateRTW?: () => void;
  onAskAI?: () => void;
  /** Hide the launcher while a dialog is open (PT-AUDIT-0147/0149/0171). */
  hidden?: boolean;
}

export function QuickActionsMenu({ allowedPaths, onNavigate, onCreatePNR, onCreateRTW, onAskAI, hidden = false }: QuickActionsMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          setIsOpen(false);
          window.setTimeout(() => triggerRef.current?.focus(), 0);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      const focusTimer = window.setTimeout(() => {
        menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
      }, 0);
      return () => {
        window.clearTimeout(focusTimer);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isOpen]);

  const actions: QuickAction[] = [
    {
      id: 'pnr',
      label: getQuickActionById('pnr')?.label ?? 'Booking Manager',
      icon: FileText,
      category: 'booking',
      permissionPaths: ['/pnr'],
      action: () => {
        if (onCreatePNR) {
          onCreatePNR();
        } else {
          onNavigate(getQuickActionById('pnr')?.path ?? '/pnr');
        }
        setIsOpen(false);
      }
    },
    {
      id: 'rtw',
      label: getQuickActionById('rtw')?.label ?? 'RTW Planner',
      icon: Plane,
      category: 'planning',
      permissionPaths: ['/rtw'],
      action: () => {
        if (onCreateRTW) {
          onCreateRTW();
        } else {
          onNavigate(getQuickActionById('rtw')?.path ?? '/rtw');
        }
        setIsOpen(false);
      }
    },
    {
      id: 'ai-assistant',
      label: getQuickActionById('ai-assistant')?.label ?? 'AI Assistant',
      icon: Sparkles,
      category: 'ai',
      permissionPaths: ['/ai-assistant'],
      action: () => {
        if (onAskAI) {
          onAskAI();
        } else {
          onNavigate(getQuickActionById('ai-assistant')?.path ?? '/ai-assistant');
        }
        setIsOpen(false);
      }
    },
    {
      id: 'finance',
      label: getQuickActionById('finance')?.label ?? 'Finance Dashboard',
      icon: DollarSign,
      category: 'finance',
      permissionPaths: ['/finance'],
      action: () => {
        onNavigate(getQuickActionById('finance')?.path ?? '/finance');
        setIsOpen(false);
      }
    },
    {
      id: 'pnt',
      label: getQuickActionById('pnt')?.label ?? 'PNT Tracking',
      icon: Briefcase,
      category: 'booking',
      permissionPaths: ['/pnt'],
      action: () => {
        onNavigate(getQuickActionById('pnt')?.path ?? '/pnt');
        setIsOpen(false);
      }
    },
    {
      id: 'settings',
      label: getQuickActionById('settings')?.label ?? 'Settings',
      icon: Settings,
      category: 'other',
      permissionPaths: ['/settings'],
      action: () => {
        onNavigate(getQuickActionById('settings')?.path ?? '/settings');
        setIsOpen(false);
      }
    }
  ];

  const allowedActions = actions.filter((action) => (
    areNavigationPathsAllowed(allowedPaths, action.permissionPaths)
  ));

  const groupedActions = allowedActions.reduce((acc, action) => {
    if (!acc[action.category]) {
      acc[action.category] = [];
    }
    acc[action.category].push(action);
    return acc;
  }, {} as Record<string, QuickAction[]>);

  const categoryLabels: Record<string, string> = {
    booking: 'Bookings',
    planning: 'Planning',
    ai: 'AI & Automation',
    finance: 'Finance',
    other: 'Other'
  };

  if (allowedActions.length === 0) return null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
        data-floating-launcher="quick-actions"
        data-launcher-hidden={hidden ? 'true' : undefined}
        tabIndex={hidden ? -1 : undefined}
        className="fixed bottom-4 right-4 z-50 h-12 w-12 max-md:h-10 max-md:w-10 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 active:scale-95 transition-all flex items-center justify-center group touch-manipulation"
        aria-label="Quick actions"
        aria-controls="quick-actions-menu"
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        {isOpen ? (
          <X className="w-5 h-5 md:w-6 md:h-6" />
        ) : (
          <Plus className="w-5 h-5 md:w-6 md:h-6" />
        )}
      </button>

      {isOpen && (
        <div
          id="quick-actions-menu"
          ref={menuRef}
          role="menu"
          aria-label="Quick actions"
          className="fixed bottom-20 right-4 z-50 w-[calc(100vw-2rem)] sm:w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <h3 className="font-semibold text-lg">Quick Actions</h3>
            <p className="text-sm text-blue-100 mt-1">Jump to common tasks</p>
          </div>

          <div className="max-h-96 overflow-y-auto p-2">
            {Object.entries(groupedActions).map(([category, categoryActions]) => (
              <div key={category} className="mb-4 last:mb-0">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  {categoryLabels[category]}
                </div>
                {categoryActions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.id}
                      onClick={action.action}
                      role="menuitem"
                      className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                    >
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-900">{action.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}
